import { useEffect, useState, useRef } from 'react';
import { supabase } from '@/shared/lib/supabase';
import type { FutebolBolaoPalpite } from '@/shared/types';

/** Teto de toasts visíveis simultâneos (evita crescimento ilimitado sob pico). */
const MAX_TOASTS = 6;

/**
 * Toasts efêmeros de novas apostas do bolão na TV.
 * Canal longo: cbRef + removeChannel; timeouts limpos no unmount.
 *
 * Não reutiliza useBolaoAoVivo: ticker não tem fixtureId, não faz fetch/catch-up
 * e remove cada INSERT após 6 s (lista acumulada ≠ toasts).
 */
export function useBolaoTicker(telaId: string): { apostas: FutebolBolaoPalpite[] } {
  const [apostas, setApostas] = useState<FutebolBolaoPalpite[]>([]);
  const timeoutsRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  const cbRef = useRef({ setApostas });
  useEffect(() => { cbRef.current = { setApostas }; });

  useEffect(() => {
    if (!telaId) return;
    const timeouts = timeoutsRef.current;

    const channel = supabase
      .channel(`bolao_ticker_${telaId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'futebol_bolao_palpites',
          filter: `tela_id=eq.${telaId}`,
        },
        (payload) => {
          const novoPalpite = payload.new as FutebolBolaoPalpite;
          cbRef.current.setApostas((prev) => [...prev, novoPalpite].slice(-MAX_TOASTS));

          const t = setTimeout(() => {
            timeouts.delete(t);
            cbRef.current.setApostas((prev) => prev.filter((p) => p.id !== novoPalpite.id));
          }, 6000);
          timeouts.add(t);
        },
      )
      .subscribe();

    return () => {
      timeouts.forEach(clearTimeout);
      timeouts.clear();
      supabase.removeChannel(channel).catch(() => {});
    };
  }, [telaId]);

  return { apostas };
}
